import { createEffect, createMemo, createSignal, For, on, onMount, Show, untrack } from 'solid-js';
import { useSearchParams } from '@solidjs/router';
import Text from '../components/Text';
import ItemStackView from '../components/ItemStackView';
import StorageSelect from '../components/StorageSelect';
import { EmptyState } from '../components/EmptyState';
import { Skeleton } from '../components/Skeleton';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useItemStacks } from '../hooks/useItemStacks';
import { useItemName } from '../hooks/useItemName';
import { formatQuantity, formatQuantityCompact } from '../services/quantityFormatter';
import { useInventoryApi, type CustomItemDefinition, type ExternalDemandGroup, type StorageInfo } from '../services/inventoryApi';
import shared from './storage/storageShared.module.css';
import styles from './InventoryExternalDemand.module.css';

export default function InventoryExternalDemand() {
    const { t } = useTranslation();
    const toast = useToast();
    const inv = useInventoryApi();
    const { stackSize } = useItemName();
    const [searchParams, setSearchParams] = useSearchParams();

    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [storagesLoading, setStoragesLoading] = createSignal(true);
    const [storageId, setStorageId] = createSignal('');
    const [groups, setGroups] = createSignal<ExternalDemandGroup[]>([]);
    const [demandLoading, setDemandLoading] = createSignal(false);

    const [customItems, setCustomItems] = createSignal<CustomItemDefinition[]>([]);
    const { nameFor, itemStack } = useItemStacks(customItems);

    // Glass is needed by every pane and stained block; past this many the chips fold behind a count.
    const NEEDED_FOR_SHOWN = 4;
    const [unfoldedRows, setUnfoldedRows] = createSignal<Set<string>>(new Set());
    const rowKey = (group: ExternalDemandGroup, itemKey: string) => `${group.sourceStorageId}|${itemKey}`;
    const toggleUnfolded = (key: string) => setUnfoldedRows(current => {
        const next = new Set(current);
        if (!next.delete(key)) next.add(key);
        return next;
    });

    const storageName = (id: string) => storages().find(storage => storage.id === id)?.name ?? t('STORAGE_STOCK_SOURCE_HIDDEN');
    const sourceName = (group: ExternalDemandGroup) => group.sourceStorageName ?? t('STORAGE_STOCK_SOURCE_HIDDEN');
    const groupsWithFindings = createMemo(() =>
        groups().filter(group => group.demand.length > 0 || group.uncraftable.length > 0 || !!group.sourceError));

    async function loadDemand(requestedStorageId: string) {
        setDemandLoading(true);
        try {
            const demandGroups = await inv.fetchExternalDemand(requestedStorageId);
            if (requestedStorageId === storageId()) setGroups(demandGroups);
        } catch (e: any) {
            if (requestedStorageId === storageId()) setGroups([]);
            toast?.error(e?.message ?? t('INVENTORY_DEMAND_LOAD_FAILED', storageName(requestedStorageId)));
        } finally {
            if (requestedStorageId === storageId()) setDemandLoading(false);
        }
    }

    createEffect(on(storageId, (selectedStorageId) => {
        setGroups([]);
        if (!selectedStorageId) return;
        untrack(() => { void loadDemand(selectedStorageId); });
    }));

    const selectStorage = (id: string) => {
        setStorageId(id);
        setSearchParams({ storage: id }, { replace: true });
    };

    onMount(async () => {
        inv.fetchCustomItems().then(setCustomItems).catch(() => {});
        try {
            const storageList = await inv.fetchStorages();
            setStorages(storageList);
            const rememberedStorageId = searchParams.storage as string | undefined;
            const initialStorage = storageList.find(storage => storage.id === rememberedStorageId) ?? storageList[0];
            if (initialStorage) selectStorage(initialStorage.id);
        } catch (e: any) {
            toast?.error(e?.message ?? t('INVENTORY_CRAFT_STORAGES_FAILED'));
        } finally {
            setStoragesLoading(false);
        }
    });

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="INVENTORY_DEMAND_TITLE" /></h1>
                <Show when={storages().length > 0}>
                    <div class={styles.headerActions}>
                        <div class={styles.storagePicker}>
                            <StorageSelect storages={storages()} value={storageId()} onChange={selectStorage} />
                        </div>
                        <button
                            type="button"
                            class={`btn btn-secondary ${styles.refresh}`}
                            disabled={demandLoading() || !storageId()}
                            onClick={() => void loadDemand(storageId())}
                        >
                            <i class={`fa-solid fa-rotate ${demandLoading() ? 'fa-spin' : ''}`} /> <Text val="REFRESH" />
                        </button>
                    </div>
                </Show>
            </div>
            <p class={styles.subtitle}><Text val="INVENTORY_DEMAND_SUBTITLE" /></p>

            <Show
                when={!storagesLoading() && !demandLoading()}
                fallback={
                    <div class={styles.groups}>
                        <div class="fcard"><Skeleton variant="text" count={4} /></div>
                        <div class="fcard"><Skeleton variant="text" count={2} /></div>
                    </div>
                }
            >
                <Show
                    when={storages().length > 0}
                    fallback={<EmptyState
                        icon="fa-boxes-stacked"
                        title={t('INVENTORY_CRAFT_NO_STORAGE_TITLE')}
                        hint={t('INVENTORY_DEMAND_NO_STORAGE_HINT')}
                    />}
                >
                    <Show
                        when={groupsWithFindings().length > 0}
                        fallback={<EmptyState
                            icon="fa-circle-check"
                            title={t('INVENTORY_DEMAND_EMPTY_TITLE')}
                            hint={t('INVENTORY_DEMAND_EMPTY_HINT')}
                        />}
                    >
                        <div class={styles.groups}>
                            <For each={groupsWithFindings()}>
                                {(group) => (
                                    <section class={`fcard ${styles.card}`}>
                                        <div class={styles.cardHead}>
                                            <i class={`fa-solid ${group.sourceStorageId === storageId() ? 'fa-box' : 'fa-truck-ramp-box'} ${styles.sourceIcon}`} />
                                            <h2 class={styles.sourceName}>{t('INVENTORY_DEMAND_FROM', sourceName(group))}</h2>
                                        </div>

                                        <Show when={group.sourceError}>
                                            {(sourceError) => <p class={styles.blocking}><i class="fa-solid fa-triangle-exclamation" /> {sourceError()}</p>}
                                        </Show>

                                        <Show when={group.demand.length > 0}>
                                            <div class={shared.tableWrap}>
                                                <table class={`${shared.table} ${styles.demandTable}`}>
                                                    <thead>
                                                        <tr>
                                                            <th><Text val="INVENTORY_DEMAND_COL_ITEM" /></th>
                                                            <th class={`${shared.thRight} ${styles.colQuantity}`}><Text val="INVENTORY_DEMAND_COL_NEEDED" /></th>
                                                            <th class={`${shared.thRight} ${styles.colQuantity}`}><Text val="INVENTORY_DEMAND_COL_AVAILABLE" /></th>
                                                            <th class={`${shared.thRight} ${styles.colQuantity}`}><Text val="INVENTORY_DEMAND_COL_MISSING" /></th>
                                                            <th><Text val="INVENTORY_DEMAND_COL_NEEDED_FOR" /></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <For each={group.demand}>
                                                            {(row) => (
                                                                <tr>
                                                                    <td>
                                                                        <div class={styles.itemCell}>
                                                                            <ItemStackView stack={itemStack(row.itemKey)} size={24} bare showTooltip />
                                                                            <span class={styles.itemName}>{nameFor(row.itemKey)}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td class={styles.number} title={formatQuantity(row.needed, stackSize(row.itemKey))}>
                                                                        {formatQuantityCompact(row.needed, stackSize(row.itemKey))}
                                                                    </td>
                                                                    <td class={styles.number} title={formatQuantity(row.available, stackSize(row.itemKey))}>
                                                                        {formatQuantityCompact(row.available, stackSize(row.itemKey))}
                                                                    </td>
                                                                    <td class={`${styles.number} ${styles.missing}`} title={formatQuantity(row.missing, stackSize(row.itemKey))}>
                                                                        {formatQuantityCompact(row.missing, stackSize(row.itemKey))}
                                                                    </td>
                                                                    <td>
                                                                        <div class={styles.neededFor}>
                                                                            <For each={unfoldedRows().has(rowKey(group, row.itemKey)) ? row.neededFor : row.neededFor.slice(0, NEEDED_FOR_SHOWN)}>
                                                                                {(line) => (
                                                                                    <span class={styles.neededForLine}>
                                                                                        <ItemStackView stack={itemStack(line.itemKey)} size={16} bare />
                                                                                        {nameFor(line.itemKey)}
                                                                                    </span>
                                                                                )}
                                                                            </For>
                                                                            <Show when={row.neededFor.length > NEEDED_FOR_SHOWN}>
                                                                                <button type="button" class={`${styles.neededForLine} ${styles.neededForMore}`} onClick={() => toggleUnfolded(rowKey(group, row.itemKey))}>
                                                                                    {unfoldedRows().has(rowKey(group, row.itemKey))
                                                                                        ? t('INVENTORY_DEMAND_NEEDED_FOR_LESS')
                                                                                        : t('INVENTORY_DEMAND_NEEDED_FOR_MORE', row.neededFor.length - NEEDED_FOR_SHOWN)}
                                                                                </button>
                                                                            </Show>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </For>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Show>

                                        <Show when={group.uncraftable.length > 0}>
                                            <div class={styles.uncraftable}>
                                                <span class={styles.uncraftableTitle}><Text val="INVENTORY_DEMAND_UNCRAFTABLE_TITLE" /></span>
                                                <ul class={styles.uncraftableList}>
                                                    <For each={group.uncraftable}>
                                                        {(line) => (
                                                            <li class={styles.uncraftableLine}>
                                                                <ItemStackView stack={itemStack(line.itemKey)} size={20} bare showTooltip />
                                                                <span class={styles.uncraftableName}>{nameFor(line.itemKey)}</span>
                                                                <span class={styles.uncraftableQuantity}>{formatQuantity(line.quantity, stackSize(line.itemKey))}</span>
                                                                <span class={styles.uncraftableError}>{line.error}</span>
                                                            </li>
                                                        )}
                                                    </For>
                                                </ul>
                                            </div>
                                        </Show>
                                    </section>
                                )}
                            </For>
                        </div>
                    </Show>
                </Show>
            </Show>
        </div>
    );
}
